/**
 * QorTrace Mega Menu — premium dropdown navigation.
 *
 * Built on Radix NavigationMenu. Five grouped dropdowns + Pricing link
 * + Consult / Sign-in buttons. Glass-morphism panels, two-column layouts
 * with icon + headline + supporting line per item, rich hover states,
 * keyboard-accessible.
 *
 * Used by Header.jsx (replaces the flat nav row).
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  ShieldCheck, Wallet, FileCode2, BadgeCheck, Sparkles, Globe2,
  BookOpen, GraduationCap, MessageCircleQuestion, TrendingUp, Code2, Newspaper,
  Cpu, Atom, Radio, Bell, Clock, Code, Network, Activity,
  ScrollText, FileSignature, Lock, ExternalLink, ChevronDown,
  ScanSearch, Wrench, Briefcase, Workflow, Calendar, Layers,
} from "lucide-react";

// --------------------------------------------------------------------- ##
// Catalog — single source of truth for every dropdown.
// --------------------------------------------------------------------- ##
const MENU = [
  // ──────────────────────────────────────────────────────────────────── ##
  // Iter269 — "Self-Serve" (formerly "Products"). Buyer journey: anyone
  // who wants a Stripe Checkout button. Strict rule: every item here has
  // a self-serve path to payment. Engagement-only items (Hybrid Sig,
  // EIP-7702, MPC Audit) live under Enterprise — zero duplication.
  // ──────────────────────────────────────────────────────────────────── ##
  {
    key: "products",
    label: "Self-Serve",
    width: 760,
    columns: [
      {
        title: "AUDIT YOUR CODE",
        items: [
          { to: "/audit",     icon: FileCode2,    title: "Smart Contract Audits",
            sub: "AI + senior reviewer · signed certificate · $149 / one-shot" },
          { to: "/free-scan", icon: Sparkles,     title: "Free PQC Scan",
            sub: "Instant cryptographic exposure snapshot · no card required" },
          { to: "/pricing/audits", icon: BadgeCheck, title: "Audit Plans",
            sub: "Builder $99/mo · Team $199/mo · Bulk credits" },
        ],
      },
      {
        title: "MONITOR YOUR PORTFOLIO",
        items: [
          { to: "/atlas",     icon: Globe2,       title: "Atlas Treasury Monitor",
            sub: "Continuous PQC + risk monitoring across portfolios" },
          { to: "/scan",      icon: Wallet,       title: "PQC Wallet Scan",
            sub: "Cryptographic posture for any address · free first scan" },
          { to: "/labs/mpc-vendor-matrix", icon: Network, title: "MPC Vendor Matrix",
            sub: "Subscription · 15+ MPC vendors scored on PQC readiness.",
            badge: "NEW" },
          { to: "/threat-radar/embed", icon: Code, title: "Threat Radar Embed",
            sub: "Free Q-Day countdown widget for your docs + partner sites" },
        ],
      },
    ],
    cta: { to: "/pricing", label: "Compare plans →" },
  },

  // ──────────────────────────────────────────────────────────────────── ##
  // Iter269 — "Enterprise" (formerly "Labs"). Buyer journey: procurement /
  // CISO / protocol lead who needs a signed deliverable. Strict rule:
  // every item here is sales-assisted, scoped per engagement, $12k+. The
  // PQC Audit Suite is the headline product — the only audit family in
  // the world that bundles all three moat-deepener PQC audits.
  // ──────────────────────────────────────────────────────────────────── ##
  {
    key: "labs",
    label: "Enterprise",
    badge: "AUDITS",
    badgeColor: "#1FDD83",
    width: 880,
    columns: [
      {
        title: "PQC AUDIT SUITE · MOAT-DEEPENERS",
        items: [
          { to: "/pricing/enterprise#pqc-suite", icon: Sparkles,
            title: "PQC Audit Suite",
            sub: "Bundle any 2 of 3 — save 15%. All three — save 25%.",
            badge: "BUNDLE" },
          { to: "/labs/services/hybrid-signature-transition-audit", icon: Atom,
            title: "Hybrid Signature Transition Audit",
            sub: "The world's first audit for protocols on a hybrid PQ+classical scheme.",
            badge: "NEW" },
          { to: "/labs/services/eip-7702-aa-pqc-audit", icon: Cpu,
            title: "EIP-7702 / AA PQC Audit",
            sub: "ERC-4337 + 7579 + 7702 — UserOps, validators, session keys, recovery.",
            badge: "NEW" },
          { to: "/labs/services/mpc-wallet-pqc-compatibility-checker", icon: Network,
            title: "MPC Wallet PQC Checker",
            sub: "Fireblocks · Coinbase · Privy · Lit · Web3Auth — every MPC stack.",
            badge: "NEW" },
        ],
      },
      {
        title: "FOUNDATIONAL ENGAGEMENTS",
        items: [
          { to: "/labs/services/pqc-cryptographic-inventory", icon: ScanSearch,
            title: "PQC Cryptographic Inventory",
            sub: "See every crypto primitive your org depends on — before Q-Day does." },
          { to: "/labs/services/pqc-migration-engineering", icon: Wrench,
            title: "PQC Migration Engineering",
            sub: "Hybrid TLS · ML-KEM · ML-DSA — production-grade, week one." },
          { to: "/labs/services/smart-contract-pqc-audit", icon: ShieldCheck,
            title: "Smart-Contract PQC Audit",
            sub: "ECDSA-locked contracts have a Q-Day half-life. Find yours." },
          { to: "/labs/services/ciso-pqc-readiness-workshop", icon: Briefcase,
            title: "CISO PQC Readiness Workshop",
            sub: "Two days, your exec team, a real PQC strategy." },
        ],
      },
      {
        title: "ABOUT THE PRACTICE",
        items: [
          { to: "/labs/methodology", icon: Workflow,      title: "Methodology",
            sub: "Discover · Scope · Engineer · Sign — four predictable phases." },
          { to: "/labs/engagements", icon: FileSignature, title: "Engagements",
            sub: "Real client work, NDA-redacted. Names on request." },
          { to: "/labs/manifesto",   icon: ScrollText,    title: "Manifesto",
            sub: "Why QorTrace Labs exists — and what we sign on the way out." },
          { to: "/labs?schedule=1&source=labs-mega-menu", icon: Calendar,
            title: "Schedule a Call",
            sub: "One business day to a senior cryptographic engineer." },
        ],
      },
    ],
    cta: { to: "/labs", label: "Open QorTrace Labs →" },
  },
  {
    key: "threat-radar",
    label: "Threat Radar",
    badge: "LIVE",
    badgeColor: "#FF2A2A",
    width: 720,
    columns: [
      {
        title: "LIVE INTELLIGENCE",
        items: [
          { to: "/threat-radar",       icon: Globe2,    title: "Live Threat Map",
            sub: "3D globe of CVE/KEV pulses + quantum facilities" },
          { to: "/q-day",              icon: Activity,  title: "Q-Day Tax Clock",
            sub: "Live encryption-debt counter since FIPS 203/204/205 finalised",
            badge: "NEW" },
          { to: "/threat-radar",       icon: Clock,     title: "Q-Day Countdown",
            sub: "Live clock to estimated cryptographic-quantum arrival" },
          { to: "/threat-radar",       icon: Atom,      title: "Quantum Facility Tracker",
            sub: "State of the art — IBM, Google, IonQ, Quantinuum, USTC, Atom" },
          { to: "/threat-radar/exposed-wallets", icon: Radio, title: "Onchain HNDL Tracker",
            sub: "Live $-at-risk on exposed keys · Satoshi Watcher" },
        ],
      },
      {
        title: "FOR YOUR SITE",
        items: [
          { to: "/threat-radar/embed", icon: Code,      title: "Embed Widget",
            sub: "iframe-able Q-Day clock for partners & docs" },
          { to: "/threat-radar/stats", icon: TrendingUp, title: "Live Stats",
            sub: "Real momentum numbers · subscribers · advisories · sigs" },
          { to: "/scan/bulk",          icon: Layers,    title: "Bulk HNDL Scanner",
            sub: "Paste 50 addresses · one rolled-up exposure report" },
          { to: "/threat-radar",       icon: Bell,      title: "Threat Briefing",
            sub: "Free weekly digest of critical advisories" },
          { to: "/api/public/marketing/threat-radar-demo.mp4",
            icon: ExternalLink, title: "Demo Clip (30s)", external: true,
            sub: "Press kit / investor deck / LinkedIn — H.264 1080p" },
        ],
      },
    ],
    cta: { to: "/threat-radar", label: "Open the Radar →" },
  },
  {
    key: "resources",
    label: "Resources",
    width: 720,
    columns: [
      {
        title: "LEARN",
        items: [
          { to: "/docs",         icon: BookOpen,                  title: "Documentation",
            sub: "Wikis, primers, deep-dives — versioned & RSS" },
          { to: "/methodology",  icon: GraduationCap,             title: "Audit Methodology",
            sub: "qortrace-method-v0.2 — public + reviewer-cite-able" },
          { to: "/help",         icon: MessageCircleQuestion,     title: "Help Center",
            sub: "Qelli AI answers · multi-turn · grounded in our docs" },
          { to: "/docs/sdks",    icon: Code2,                     title: "SDK Samples",
            sub: "Python · TypeScript · Solidity hooks" },
        ],
      },
      {
        title: "INSIGHTS",
        items: [
          { to: "/trends",       icon: TrendingUp,    title: "Community Pulse",
            sub: "What teams are scanning, audited, asking" },
          { to: "/whats-new",    icon: Newspaper,     title: "What's New",
            sub: "Release notes · iter feed · roadmap signals" },
          { to: "/releases",     icon: ScrollText,    title: "Release Log",
            sub: "Hash-chained · tamper-evident · Atom + RSS",
            badge: "NEW" },
        ],
      },
    ],
    cta: { to: "/docs", label: "Browse all docs →",
           secondary: { to: "/account/nda?view=create",
                        label: "Draft mutual NDA →",
                        icon: FileSignature } },
  },
  {
    key: "trust",
    label: "Trust",
    width: 380,
    columns: [
      {
        title: "TRUST CENTER",
        items: [
          { to: "/trust",                icon: ShieldCheck, title: "Trust Center",
            sub: "Live SOC 2 / ISO / NIST / DORA / FFIEC posture" },
          { to: "/legal/security",       icon: Lock,        title: "Security",
            sub: "Architecture, data flow, responsible disclosure" },
          { to: "/verify",               icon: BadgeCheck,  title: "Verify a Certificate",
            sub: "Public lookup of any QorTrace audit cert" },
          { to: "/legal/privacy",        icon: ScrollText,  title: "Privacy & Cookies",
            sub: "How we collect, store, and protect data" },
        ],
      },
    ],
    cta: { to: "/trust", label: "View posture →" },
  },

  // ──────────────────────────────────────────────────────────────────── ##
  // Phase 12.6 — Sister Brands. Only brands with their OWN live public
  // surface (own domain / own product story) belong here. Anything that's
  // a feature of QorTrace lives under Self-Serve / Enterprise.
  // Today: just QorBOM (live B2B2B partner platform).
  // ──────────────────────────────────────────────────────────────────── ##
  {
    key: "sister-brands",
    label: "Sister Brands",
    width: 380,
    columns: [
      {
        title: "BRANDS IN THE QORTRACE FAMILY",
        items: [
          { to: "https://qorbom.com",
            external: true,
            icon: Layers,
            title: "QorBOM™",
            sub: "B2B2B Cryptographic Bill-of-Materials platform · white-labeled for audit firms",
            badge: "LIVE",
          },
        ],
      },
    ],
    cta: { to: "/sister-brands",
           label: "All sister brands →" },
  },
];

const SOLO_LINKS = [
  { to: "/pricing",            label: "Pricing",  i18nKey: "nav.pricing" },
  { to: "/audit/talk-to-sales", label: "Contact", i18nKey: "nav.contact" },
];

// Iter254 — exported so MobileNav.jsx can render the same data structure
// (single source of truth, no drift between desktop + mobile nav).
export { MENU, SOLO_LINKS };

// Map MENU[].key → translation key for the top-level dropdown labels.
// We keep MENU declaratively in English so existing tests still match
// the underlying data; the trigger button text is localised here.
const MENU_I18N = {
  products:        "nav.products",
  labs:            "nav.labs",
  "threat-radar":  "nav.threat_radar",
  resources:       "nav.resources",
  trust:           "nav.trust",
  "sister-brands": "nav.sister_brands",
};

export { MENU_I18N };

// --------------------------------------------------------------------- ##
// Component
// --------------------------------------------------------------------- ##
export default function MegaMenu() {
  const { t } = useTranslation();
  const [openKey, setOpenKey] = useState(null);
  const closeTimer = React.useRef(null);

  const open = (k) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    setOpenKey(k);
  };
  const scheduleClose = () => {
    closeTimer.current = setTimeout(() => setOpenKey(null), 180);
  };

  return (
    <nav data-testid="mega-menu" style={{ position: "relative",
                                            display: "flex", alignItems: "center",
                                            gap: 4, flexWrap: "nowrap" }}>
      {MENU.map((g) => {
        const isOpen = openKey === g.key;
        return (
          <div key={g.key}
               onMouseEnter={() => open(g.key)}
               onMouseLeave={scheduleClose}
               style={{ position: "relative" }}>
            <button
              data-testid={`mega-trigger-${g.key}`}
              onClick={() => setOpenKey(isOpen ? null : g.key)}
              onFocus={() => open(g.key)}
              className="qt-btn qt-btn-ghost"
              style={{
                fontSize: 13, padding: "0.5rem 0.75rem", display: "inline-flex",
                alignItems: "center", gap: 4,
                color: isOpen ? "#fff" : undefined,
                background: isOpen ? "var(--qt-surface-hover)" : "transparent",
              }}
              aria-expanded={isOpen}
              aria-haspopup="true"
            >
              {MENU_I18N[g.key] ? t(MENU_I18N[g.key], g.label) : g.label}
              {g.badge && (
                <span className="qt-mono"
                      style={{ fontSize: 8, padding: "1px 5px", borderRadius: 2,
                               color: g.badgeColor, border: `1px solid ${g.badgeColor}`,
                               background: `${g.badgeColor}18`, fontWeight: 700,
                               letterSpacing: "0.12em" }}>
                  {g.badge}
                </span>
              )}
              <ChevronDown size={12} style={{ transition: "transform 200ms",
                                                transform: isOpen ? "rotate(180deg)" : "rotate(0)" }} />
            </button>

            {isOpen && (
              <div
                data-testid={`mega-panel-${g.key}`}
                onMouseEnter={() => open(g.key)}
                onMouseLeave={scheduleClose}
                style={{
                  position: "absolute", top: "calc(100% + 6px)", left: 0, zIndex: 60,
                  width: g.width, padding: 18,
                  background: "#04091A",
                  /* Solid background per Iter255 — no see-through nav panels */
                  border: "1px solid rgba(0,229,255,0.32)",
                  borderRadius: 4,
                  boxShadow: "0 24px 56px rgba(0,0,0,0.6), 0 0 0 1px rgba(0,229,255,0.08) inset",
                  animation: "qt-mega-fade 160ms ease-out",
                }}
              >
                <div style={{
                  display: "grid",
                  gridTemplateColumns: g.columns.length === 1 ? "1fr" : "1fr 1fr",
                  gap: 22,
                }}>
                  {g.columns.map((col) => (
                    <div key={col.title}>
                      <div className="qt-mono" style={{
                        fontSize: 9, letterSpacing: "0.18em",
                        color: "var(--qt-text-3)", fontWeight: 700,
                        marginBottom: 8,
                      }}>
                        {col.title}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {col.items.map((it) => (
                          <MenuItem key={it.to + it.title} item={it}
                                    onClick={() => setOpenKey(null)} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {g.cta && (
                  <div style={{
                    marginTop: 14, paddingTop: 12,
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 12, flexWrap: "wrap",
                  }}>
                    <Link to={g.cta.to}
                          onClick={() => setOpenKey(null)}
                          data-testid={`mega-cta-${g.key}`}
                          style={{
                            fontFamily: "ui-monospace,Menlo,monospace",
                            color: "var(--qt-cyan)", fontSize: 11,
                            letterSpacing: "0.14em", fontWeight: 700,
                            textDecoration: "none",
                          }}>
                      {g.cta.label}
                    </Link>
                    {g.cta.secondary && (
                      <Link to={g.cta.secondary.to}
                            onClick={() => setOpenKey(null)}
                            data-testid={`mega-cta-${g.key}-secondary`}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 6,
                              fontFamily: "ui-monospace,Menlo,monospace",
                              fontSize: 10.5, letterSpacing: "0.14em",
                              fontWeight: 700, textDecoration: "none",
                              color: "var(--qt-cyan)",
                              background: "rgba(0,229,255,0.08)",
                              border: "1px solid rgba(0,229,255,0.28)",
                              padding: "5px 9px", borderRadius: 2,
                              transition: "background 160ms ease, gap 180ms ease",
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(0,229,255,0.16)"; e.currentTarget.style.gap = "8px"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = "rgba(0,229,255,0.08)"; e.currentTarget.style.gap = "6px"; }}>
                        {g.cta.secondary.icon && <g.cta.secondary.icon size={11} />}
                        {g.cta.secondary.label}
                      </Link>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Solo links */}
      {SOLO_LINKS.map((l) => (
        <Link key={l.to} to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className="qt-btn qt-btn-ghost"
              style={{ fontSize: 13, padding: "0.5rem 0.75rem" }}>
          {l.i18nKey ? t(l.i18nKey, l.label) : l.label}
        </Link>
      ))}

      {/* Local keyframes */}
      <style>{`
        @keyframes qt-mega-fade {
          from { opacity: 0; transform: translateY(-4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </nav>
  );
}


function MenuItem({ item, onClick }) {
  const Ic = item.icon;
  const Common = ({ children }) => (
    <div style={{ display: "flex", gap: 10, padding: "9px 10px",
                  borderRadius: 4, transition: "background 140ms",
                  cursor: "pointer" }}
         onMouseEnter={(e) => e.currentTarget.style.background = "rgba(0,229,255,0.07)"}
         onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}>
      <div style={{ width: 28, height: 28, flexShrink: 0,
                    border: "1px solid rgba(0,229,255,0.25)",
                    background: "rgba(0,229,255,0.06)",
                    borderRadius: 4, display: "flex",
                    alignItems: "center", justifyContent: "center",
                    color: "var(--qt-cyan)" }}>
        {Ic && <Ic size={14} />}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ color: "#fff", fontSize: 13, fontWeight: 600,
                      display: "flex", alignItems: "center", gap: 6 }}>
          {item.title}
          {item.badge && (
            <span data-testid={`mega-item-badge-${item.badge.toLowerCase()}`}
                  style={{
                    fontSize: 8, fontWeight: 700, letterSpacing: "0.14em",
                    padding: "1px 5px", borderRadius: 99,
                    background: "rgba(31,221,131,0.16)",
                    color: "#1FDD83",
                    border: "1px solid rgba(31,221,131,0.4)",
                    textTransform: "uppercase",
                  }}>{item.badge}</span>
          )}
          {item.external && <ExternalLink size={10} style={{ opacity: 0.5 }} />}
        </div>
        <div style={{ color: "var(--qt-text-3)", fontSize: 11,
                      lineHeight: 1.4, marginTop: 1 }}>
          {item.sub}
        </div>
      </div>
      {children}
    </div>
  );
  if (item.external) {
    return (
      <a href={item.to} target="_blank" rel="noopener noreferrer"
         onClick={onClick}
         style={{ textDecoration: "none" }}>
        <Common />
      </a>
    );
  }
  return (
    <Link to={item.to} onClick={onClick} style={{ textDecoration: "none" }}>
      <Common />
    </Link>
  );
}
